import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Grid2, Typography, LinearProgress, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { enqueueSnackbar } from 'actions';
import { TrafficTrendsDataRefreshRequest } from '../requests/DataManagementRequests';

const TrafficTrendsDataRefreshForm = () => {
	const userId = useSelector(state => state.session.user.id);
	const dispatch = useDispatch();

	const [selectedFiles, setSelectedFiles] = useState([]);
	const [isUploading, setIsUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);

	const handleFileChange = (e) => {
		const files = Array.from(e.target.files);
		setSelectedFiles(files);
	};

	const resetForm = () => {
		setSelectedFiles([]);
		setUploadProgress(0);
	};

	const handleSubmit = async () => {
		if (selectedFiles.length === 0) {
			dispatch(enqueueSnackbar({ message: 'Please select at least one file.', options: { variant: 'error' } }));
			return;
		}

		const formData = new FormData();
		selectedFiles.forEach(file => formData.append('Files', file));
		formData.append('UserId', userId);

		try {
			setIsUploading(true);
			const response = await TrafficTrendsDataRefreshRequest(formData, (percent) => setUploadProgress(percent));
			dispatch(enqueueSnackbar({ message: response.data.message, options: { variant: 'success' } }));
			resetForm();
		} catch (error) {
			dispatch(enqueueSnackbar({ message: `${error}`, options: { variant: 'error' } }));
		} finally {
			setIsUploading(false);
		}
	};

	return (
		<Grid2 container spacing={2}>
			<Grid2 item size={12}>
				<Typography variant="body2" align="center" sx={{ color: 'text.secondary' }}>
					Please select between 4 to 8 files to upload. Once the upload is complete, the existing Traffic Trends data will be permanently replaced by the new data.
					<br />
					<br /><strong><u>Proceed at your own risk.</u></strong>
				</Typography>
			</Grid2>

			<Grid2 item size={12}>
				<Button
					variant="contained"
					component="label"
					fullWidth
					startIcon={<UploadFileIcon />}
					disabled={isUploading}
				>
					Select Files
					<input type="file" accept=".zip" multiple hidden onChange={handleFileChange} />
				</Button>
			</Grid2>

			{selectedFiles.length > 0 && (
				<Grid2 item size={12}>
					<List dense disablePadding>
						{selectedFiles.map((file, idx) => (
							<ListItem key={idx} disableGutters>
								<ListItemIcon sx={{ minWidth: 32 }}>
									<InsertDriveFileIcon fontSize="small" />
								</ListItemIcon>
								<ListItemText
									primary={file.name}
									secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
								/>
							</ListItem>
						))}
					</List>
				</Grid2>
			)}

			{isUploading && (
				<Grid2 item size={12}>
					<LinearProgress variant="determinate" value={uploadProgress} />
					<Typography variant="caption" align="center" display="block" sx={{ mt: 0.5 }}>
						{uploadProgress}%
					</Typography>
				</Grid2>
			)}

			<Grid2 item size={12}>
				<Button
					variant="contained"
					color="primary"
					fullWidth
					onClick={handleSubmit}
					disabled={isUploading || selectedFiles.length === 0}
				>
					{isUploading ? `Uploading... ${uploadProgress}%` : 'Submit'}
				</Button>
			</Grid2>
		</Grid2>
	);
};

export default TrafficTrendsDataRefreshForm;